import './game.css';
import MainMenu from './MainMenu/MainMenu';
import { useCallback, useEffect, useState } from 'react';
import IntroScene from './IntroSceneAndEpilogue/IntroScene';
import LandingPage from './LandingPage/LandingPage';
import HotDogGame from '../minigames/HotDogGame/HotDogGame';
import {
    GameState,
    MINIGAME_DONE_MESSAGES,
    defaultMinigameProgress,
} from './game_state_defs';
import PhoneGame from '../minigames/PhoneGame/PhoneGame';
import LaundryGame from '../minigames/LaundryGame/LaundryGame';
import { useBackgroundMusic } from '../hooks/useBackgroundMusic';
import Credits from './MainMenu/Credits';
import Epilogue from './IntroSceneAndEpilogue/Epilogue';

/**
 * This component manages the game state, like a router.
 */
function Game() {
    const [gameState, setGameState] = useState<GameState>(GameState.MAIN_MENU);
    const [minigameProgress, setMinigameProgress] = useState(
        defaultMinigameProgress
    );
    const [fadeOut, setFadeOut] = useState(false);
    const [dialogue, setDialogue] = useState<string[]>([]);

    const startBackgroundMusic = useBackgroundMusic();

    useEffect(() => {
        if (Object.values(minigameProgress).every(Boolean)) {
            setFadeOut(true);
            setTimeout(() => {
                setGameState(GameState.EPILOGUE);
            }, 1000);
        }
    }, [minigameProgress]);

    const getGameStateSetter = useCallback((newGameState: GameState) => {
        return () => setGameState(newGameState);
    }, []);

    const tryOpeningMinigame = useCallback(
        (minigame: GameState) => {
            if (
                minigame in minigameProgress &&
                !minigameProgress[minigame as keyof typeof minigameProgress]
            ) {
                setGameState(minigame);
            } else {
                setDialogue(
                    MINIGAME_DONE_MESSAGES[
                        minigame as keyof typeof minigameProgress
                    ]
                );
            }
        },
        [minigameProgress]
    );

    const markMinigameAsDone = useCallback(
        (minigame: GameState) => {
            if (minigame in minigameProgress) {
                const newMinigameProgress = { ...minigameProgress };
                newMinigameProgress[minigame as keyof typeof minigameProgress] =
                    true;
                setMinigameProgress(newMinigameProgress);
            }
        },
        [minigameProgress]
    );

    return (
        <div className="game-wrapper">
            <div className="aspect-ratio--16x9">
                {gameState === GameState.MAIN_MENU && (
                    <MainMenu
                        playGameCb={() => {
                            setGameState(GameState.INTRO_SCENE);
                            setMinigameProgress(defaultMinigameProgress);
                            setFadeOut(false);
                            startBackgroundMusic();
                        }}
                        openCreditsCb={getGameStateSetter(GameState.CREDITS)}
                    />
                )}
                {gameState === GameState.CREDITS && (
                    <Credits
                        goToMenuCb={getGameStateSetter(GameState.MAIN_MENU)}
                    />
                )}
                {gameState === GameState.EPILOGUE && (
                    <Epilogue
                        goToMenuCb={getGameStateSetter(GameState.MAIN_MENU)}
                    />
                )}
                {gameState === GameState.INTRO_SCENE && (
                    <IntroScene
                        continueCb={getGameStateSetter(
                            GameState.GAME_LANDING_PAGE
                        )}
                    />
                )}
                {gameState === GameState.GAME_LANDING_PAGE && (
                    <LandingPage
                        goToMenuCb={getGameStateSetter(GameState.MAIN_MENU)}
                        goToGame={tryOpeningMinigame}
                        dialogue={dialogue}
                        setDialogueCb={setDialogue}
                        fadeOut={fadeOut}
                    />
                )}
                {gameState === GameState.HOT_DOG_GAME && (
                    <HotDogGame
                        goToLandingPage={getGameStateSetter(
                            GameState.GAME_LANDING_PAGE
                        )}
                        markGameDoneCb={() =>
                            markMinigameAsDone(GameState.HOT_DOG_GAME)
                        }
                    />
                )}
                {gameState === GameState.PHONE_GAME && (
                    <PhoneGame
                        goToLandingPage={getGameStateSetter(
                            GameState.GAME_LANDING_PAGE
                        )}
                        markGameDoneCb={() =>
                            markMinigameAsDone(GameState.PHONE_GAME)
                        }
                    />
                )}
                {gameState === GameState.LAUNDRY_GAME && (
                    <LaundryGame
                        goToLandingPage={getGameStateSetter(
                            GameState.GAME_LANDING_PAGE
                        )}
                        markGameDoneCb={() =>
                            markMinigameAsDone(GameState.LAUNDRY_GAME)
                        }
                    />
                )}
            </div>
        </div>
    );
}

export default Game;
